import {
  Badge,
  Button,
  Card,
  HStack,
  Link,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import * as React from "react";
import { LuDownload, LuShare2 } from "react-icons/lu";

import { Creator } from "../api/creator";
import { Patron, getPatronUploadDownloadUrl } from "../api/patron";
import { Upload } from "../api/uploads";
import { toaster } from "../components/chakra/toaster";
import { FormatBytes } from "../components/format-bytes";
import { ThumbnailCarousel } from "../components/thumbnail-carousel";
import { rewriteCdnUrl } from "../lib/cdn";

interface UploadRowProps {
  creator: Creator;
  upload: Upload;
  patron: Patron;
}

const UploadCard = ({ creator, upload, patron }: UploadRowProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}/creators/${creator.slug}#upload-${upload.id}`;
    await navigator.clipboard.writeText(url);
    toaster.create({
      type: "success",
      title: "Link copied to clipboard",
      description: upload.title,
    });
  };

  const badgeStatus = React.useMemo(() => {
    const lastVisitedAt = patron["last-visited-at"][creator.slug];
    if (!lastVisitedAt) return null;

    const uploadCreatedAt = new Date(upload["created-at"]).getTime() / 1000;
    const uploadUpdatedAt = new Date(upload["updated-at"]).getTime() / 1000;

    if (uploadCreatedAt > lastVisitedAt) {
      return "new";
    } else if (uploadUpdatedAt > lastVisitedAt) {
      return "updated";
    }

    return null;
  }, [patron, creator.slug, upload]);

  return (
    <Card.Root
      id={`upload-${upload.id}`}
      overflow="hidden"
      variant="outline"
      transition="all 0.2s"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
      }}
    >
      <Card.Header p={0} position="relative">
        <ThumbnailCarousel
          thumbnails={upload.thumbnails.map(rewriteCdnUrl)}
          title={upload.title}
        />
      </Card.Header>
      <Card.Body gap={2}>
        <Stack>
          <Stack gap={0}>
            {badgeStatus && (
              <Badge
                colorPalette="orange"
                fontWeight="semibold"
                ml="-1"
                size="sm"
                width="fit-content"
              >
                {badgeStatus === "new" ? "NEW!" : "UPDATED"}
              </Badge>
            )}
            <HStack gap="0">
              <Text as="h3" fontSize="sm" fontWeight="semibold" lineClamp={2}>
                {upload.title}
              </Text>
              <Spacer />
              <Button
                onClick={handleShare}
                colorPalette="gray"
                variant="plain"
                aria-label="Share"
                size="sm"
              >
                <LuShare2 />
              </Button>
            </HStack>
          </Stack>
          <Text
            _hover={{ color: "gray.800" }}
            color="gray.600"
            cursor="pointer"
            fontSize="sm"
            lineClamp={isExpanded ? undefined : 2}
            onClick={() => setIsExpanded(!isExpanded)}
            transition="color 0.2s"
          >
            {upload.description}
          </Text>
          <Text color="gray.500" fontSize="sm">
            <FormatBytes value={upload.size} />
          </Text>
        </Stack>
      </Card.Body>
      <Card.Footer>
        <Stack direction="row" gap={2} width="full">
          <Link
            href={getPatronUploadDownloadUrl(upload.id)}
            flex="1"
            _hover={{ textDecoration: "none" }}
          >
            <Button width="full" colorPalette="blue">
              <Stack direction="row" align="center">
                <LuDownload />
                <Text>Download</Text>
              </Stack>
            </Button>
          </Link>
        </Stack>
      </Card.Footer>
    </Card.Root>
  );
};

interface UploadsGridProps {
  creator: Creator;
  uploads: Upload[];
  patron: Patron;
}

export const UploadsGrid = ({ creator, uploads, patron }: UploadsGridProps) => {
  return (
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
      {uploads.map((upload) => (
        <UploadCard
          key={upload.id}
          creator={creator}
          upload={upload}
          patron={patron}
        />
      ))}
    </SimpleGrid>
  );
};
